import {
  EnvironmentProviders,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { catchError, firstValueFrom, of } from 'rxjs';
import { AuthService } from '../_services/auth/auth.service';
import { LicenseService } from '../_services/license/license.service';

// Skip the silent-SSO probe when we boot inside the silent-callback iframe —
// that instance's only job is to postMessage back to the parent, and probing
// again would recurse.
export const isSilentCallbackIframe = (
  win: Window = window,
  pathname: string = window.location.pathname,
): boolean => win.parent !== win.self && pathname.endsWith('/silent-callback');

// Entry paths where a logged-in user with usable active licenses should be
// sent straight to the material instead of stopping at /licenses. Other paths
// (reset-password, register, invitations, ...) keep their dedicated UX.
export const isEntryPath = (pathname: string): boolean =>
  pathname === '/' || pathname === '' || pathname === '/login';

/**
 * Cold-boot routine for the onegate SPA:
 *   1. In the silent-callback iframe, do nothing — the parent owns the probe.
 *   2. Run the Toegang silent-SSO probe to (re-)establish an NT2 session.
 *   3. On entry paths, when the user ends up logged in with at least one
 *      active license that doesn't expire within a month, skip /licenses
 *      and replace the location with the material URL.
 *
 * All `inject()` calls must happen before the first `await`, otherwise
 * Angular's injection context is gone and inject() throws NG0203.
 */
export const runAppBootstrap = async (): Promise<void> => {
  if (isSilentCallbackIframe()) {
    return;
  }
  const auth = inject(AuthService);
  const licenseService = inject(LicenseService);
  const loggedIn = await auth.checkToegangSession();
  if (!loggedIn) {
    return;
  }
  // Best-effort profile fetch so the header can show the user's name on
  // cold boot. loadCurrentUser swallows errors and yields null.
  await firstValueFrom(auth.loadCurrentUser());
  if (!isEntryPath(window.location.pathname)) {
    return;
  }
  await firstValueFrom(
    licenseService.getLicenses().pipe(catchError(() => of([]))),
  );
  if (licenseService.hasUsableActiveLicenses()) {
    licenseService.redirectToMaterial();
    // Halt bootstrap so Angular doesn't render a flash of /login or
    // /licenses while the browser navigates away.
    await new Promise<never>(() => {
      // never resolves
    });
  }
};

export function provideAppBootstrap(): EnvironmentProviders {
  return provideAppInitializer(runAppBootstrap);
}
